import { AuthAPI } from '@/lib/pdk-auth';
import { pdkAxios } from '@/lib/pdk-axios';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

export interface UserData {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  current_hub: string;
  hubs: string[];
  avatar?: string;
  slack_connected: boolean;
  username: string;
}

export type SessionUser = UserData & {
  accessToken: string;
};

export interface AuthContext {
  isAuthenticated: boolean;
  hasValidSession: boolean;
  signIn: (credentialsJWT: string) => Promise<boolean>;
  completeSignIn: (accessToken: string) => Promise<boolean>;
  signOut: () => Promise<void>;
  user: SessionUser | null;
}

const AuthContext = createContext<AuthContext | null>(null);

const key = 'paddock.auth.user';
const lastEmailKey = 'paddock.auth.last_email';

export function getStoredUser() {
  const user = localStorage.getItem(key);
  if (user) {
    return JSON.parse(user);
  }
  return null;
}

export function getLastSignedInEmail() {
  return localStorage.getItem(lastEmailKey);
}

export function clearLastSignedInEmail() {
  localStorage.removeItem(lastEmailKey);
}

function setLastSignedInEmail(email: string | undefined) {
  if (email) {
    localStorage.setItem(lastEmailKey, email);
  }
}

function setStoredUser(user: SessionUser | null) {
  if (user) {
    localStorage.setItem(key, JSON.stringify(user));
  } else {
    localStorage.removeItem(key);
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(getStoredUser());
  const isAuthenticated = !!user;

  const [hasValidSession, setHasValidSession] = useState(false);
  const accessToken = user?.accessToken;

  useEffect(() => {
    const validateSession = async () => {
      try {
        if (accessToken) {
          await AuthAPI.validateSession(accessToken);
          setHasValidSession(true);
        } else {
          setHasValidSession(false);
        }
      } catch (error) {
        console.error('Error validating session', error);
        setHasValidSession(false);
      }
    };
    validateSession();
  }, [accessToken]);

  const signOut = useCallback(async () => {
    setStoredUser(null);
    setUser(null);
    pdkAxios.defaults.headers.common['Authorization'] = '';
  }, []);

  useEffect(() => {
    if (!user) {
      return;
    }

    pdkAxios.defaults.headers.common['Authorization'] =
      `Bearer ${user?.accessToken}`;
  }, [user]);

  const signIn = useCallback(async (credentialsJWT: string) => {
    const { user, access_token } =
      await AuthAPI.authenticateGoogle(credentialsJWT);

    if (!user || !access_token) {
      // todo: tanstack migration
      // todo: handle this error better
      return false;
    }

    // ensure that if the user is not from ever, they cannot login
    // if the domain does not include @evercars.com, they cannot login
    if (!user.email.includes('@evercars.com')) {
      return false;
    }

    const sessionUser = {
      ...user,
      accessToken: access_token,
    };

    setUser(sessionUser);
    setStoredUser(sessionUser);
    setLastSignedInEmail(sessionUser.email);

    return true;
  }, []);

  // Redirect-mode entry point: the backend already exchanged the Google
  // credential for our session JWT, so all we need to do is hydrate the user
  // via /validate and seed local state.
  const completeSignIn = useCallback(async (accessToken: string) => {
    try {
      const user = await AuthAPI.validateSession(accessToken);
      if (!user.email?.includes('@evercars.com')) {
        return false;
      }
      const sessionUser = { ...user, accessToken };
      setUser(sessionUser);
      setStoredUser(sessionUser);
      setLastSignedInEmail(sessionUser.email);
      return true;
    } catch (err) {
      console.error('completeSignIn failed', err);
      return false;
    }
  }, []);

  useEffect(() => {
    setUser(getStoredUser());
  }, []);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        signIn,
        completeSignIn,
        signOut,
        hasValidSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
